// Auto-save: subscribes to projectStore and debounces a PUT /api/projects/:id
// call 1 second after any project mutation. Updates uiStore.isSaving and
// uiStore.lastSaved so the TopBar can show save status.

import { useProjectStore } from '../store/projectStore';
import { useUiStore } from '../store/uiStore';

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

async function persistProject(): Promise<void> {
  const project = useProjectStore.getState().project;
  if (!project) return;

  const { setIsSaving, setLastSaved, setSaveError } = useUiStore.getState();
  setIsSaving(true);

  try {
    const res = await fetch(`/api/projects/${project.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    setLastSaved(new Date().toISOString());
  } catch (err) {
    console.error('[autoSave] save failed:', err);
    setSaveError('Auto-save failed — check connection');
  } finally {
    setIsSaving(false);
  }
}

/**
 * Start watching the project store for mutations and auto-saving to D1.
 * Returns a cleanup function that cancels any pending save and stops watching.
 * Call once at application startup.
 */
export function initAutoSave(): () => void {
  const unsubscribe = useProjectStore.subscribe((state, prevState) => {
    // Only act when the project object reference actually changed.
    if (state.project === prevState.project) return;
    // If the project was cleared, cancel any pending save and stop.
    if (!state.project) {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      return;
    }

    // Reset the debounce window on every mutation.
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void persistProject();
    }, 1000);
  });

  return () => {
    unsubscribe();
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };
}
